/* prettier-ignore-file */
/**
 * Central design tokens — "Coastal Slate".
 *
 * Before this file the app had NO shared theme: 4,176 hardcoded colour literals
 * across 147 distinct values in 68 files, including three different greens
 * (#28a745, #4caf50, #34c759), two reds (#dc3545, #ff3b30) and two ambers
 * (#ff9500, #ffc107). That is what made the UI read as "multicoloured".
 *
 * The palette below collapses those onto one neutral ramp plus a single primary
 * and one colour per semantic meaning. Colour now carries information: teal means
 * "the main action", green/red/amber mean success/danger/warning, and everything
 * else is neutral.
 *
 * CHANGING THE LOOK: edit `colors` here, then re-run scripts/retheme.js to push
 * the new values through the legacy StyleSheet blocks. New code should import
 * from this file directly rather than hardcoding.
 *
 * NOTE: HTML email/print templates (Settings.js ~4243, CheckIn.js ~1095) are
 * deliberately NOT themed — they render in mail clients that cannot reach these
 * tokens, so their inline styles stay as-is.
 */

export const colors = {
  // --- Neutral ramp (slate, very slightly warm) ---
  ink900: "#0F1A19", // headers, highest-contrast text  (was #000)
  ink700: "#2E3A38", // primary body text               (was #333)
  ink500: "#5C6B68", // secondary text                  (was #666)
  ink400: "#8A9794", // muted text, meta                (was #999)
  ink300: "#AEB8B6", // placeholder text                (was #bbb)

  border: "#DDE3E2", // default borders                 (was #ddd/#e0e0e0/#ccc)
  borderSubtle: "#EDF1F0", // hairlines, dividers       (was #f0f0f0/#eee)

  bg: "#F6F8F7", // app background                      (was #f5f5f5)
  surface: "#FFFFFF", // cards, sheets
  surfaceMuted: "#F4F6F6", // inset/secondary surfaces  (was #f8f9fa)

  // --- Primary ---
  primary: "#0E7C6B", // main action                    (was #007AFF)
  primaryDark: "#0A5F52", // pressed / emphasis
  primarySoft: "#E3F2EF", // tinted backgrounds         (was #e3f0ff)
  primaryDisabled: "#9FC5BE", // disabled primary

  // --- Semantic: one colour each ---
  success: "#1F8A5B", //                                (was #28a745/#4caf50/#34c759)
  successSoft: "#E4F4EC",
  danger: "#C4453D", //                                 (was #dc3545/#ff3b30)
  dangerSoft: "#FBEAE9",
  warning: "#B4761B", //                                (was #ff9500/#ffc107/#FFCC00)
  warningSoft: "#FDF3E3",
  warningInk: "#7A4E12", //                             (was #856404)

  // --- Misc ---
  disabled: "#C9D1D0",
  overlay: "rgba(15, 26, 25, 0.55)",
};

export const spacing = { xs: 4, sm: 8, md: 12, lg: 16, xl: 24, xxl: 32 };

/**
 * Deliberately tight. The app previously leaned on 10-16px radii everywhere, which
 * read as soft/dated; crisper corners feel more current. `pill` stays fully round
 * for chips and circular controls.
 * Applied to legacy StyleSheets by scripts/reradius.js, which skips any radius that
 * is half of its element's width/height (i.e. an intentional circle).
 */
export const radius = { sm: 4, md: 8, lg: 10, pill: 999 };

export const typography = {
  title: { fontSize: 20, fontWeight: "700", color: colors.ink900 },
  heading: { fontSize: 16, fontWeight: "700", color: colors.ink700 },
  body: { fontSize: 14, color: colors.ink700 },
  meta: { fontSize: 12, color: colors.ink500 },
  small: { fontSize: 11, color: colors.ink400 },
};

/**
 * Elevation scale, expressed as LAYERED shadows.
 *
 * React Native's shadowColor/Offset/Opacity/Radius can only express one shadow, which
 * is why the old UI read as flat: a single 4px blur at 0.1 opacity is either invisible
 * or muddy. react-native-web accepts `boxShadow` (View/types.js.flow:183), so we use
 * two layers — a tight contact shadow for the edge, plus a wide ambient one for depth.
 * That pairing is what makes a surface look lifted rather than outlined.
 *
 * `elevation` is kept alongside for React Native parity; it is ignored on web.
 */
const ambient = (y, blur, alpha) => `0 ${y}px ${blur}px rgba(15, 26, 25, ${alpha})`;

export const elevation = {
  // Resting cards and list items.
  sm: `${ambient(1, 2, 0.06)}, ${ambient(2, 8, 0.06)}`,
  // Buttons, raised panels, anything interactive at rest.
  md: `${ambient(1, 3, 0.07)}, ${ambient(6, 16, 0.08)}`,
  // Hovered controls, popovers, dropdown menus.
  lg: `${ambient(2, 6, 0.08)}, ${ambient(16, 40, 0.12)}`,
  // Modals and sheets.
  xl: `${ambient(4, 12, 0.1)}, ${ambient(28, 64, 0.18)}`,
};

/** Top highlight that gives solid surfaces a subtle sheen rather than a flat fill. */
export const sheen = {
  onDark: "inset 0 1px 0 rgba(255, 255, 255, 0.16)",
  onLight: "inset 0 1px 0 rgba(255, 255, 255, 0.7)",
};

export const shadows = {
  card: { boxShadow: elevation.sm, elevation: 2 },
  raised: { boxShadow: elevation.lg, elevation: 5 },
};

export default { colors, spacing, radius, typography, shadows, elevation, sheen };
